import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { useTranslation } from 'react-i18next';

interface SudokuBoardProps {
  grid: number[][];
  originalGrid: number[][];
  solution: number[][];
  onCellChange: (row: number, col: number, value: number) => void;
  onPencilMarksChange: (marks: Record<string, number[]>, isManualEntry?: boolean, isError?: boolean, isRemoving?: boolean) => void;
  pencilMarks: Record<string, number[]>;
  onHint: () => void;
  hintedCell: {row: number, col: number} | null;
  onResetGame: (skipConfirmation?: boolean) => boolean;
  onPencilError: () => void;
  onAutoPencilUsed: () => void;
  onCoverageUsed: () => void;
  numberCounts: Record<number, number>;
  isPaused: boolean;
  isComplete?: boolean;
  onSectionComplete: () => void;
  playTurnOnSound: () => void;
  playTurnOffSound: () => void;
  playWritePencilSound: () => void;
  isDialogOpen?: boolean;
  highlightNumber: number | null;
  onHighlightNumberChange: (number: number | null) => void;
}

export interface SudokuBoardHandle {
  resetSelection: () => void;
  setCompletionProcessing: (value: boolean) => void;
}

export const SudokuBoard = forwardRef<SudokuBoardHandle, SudokuBoardProps>(({
  grid,
  originalGrid,
  solution,
  onCellChange,
  onPencilMarksChange,
  pencilMarks,
  onHint,
  hintedCell,
  onPencilError,
  onAutoPencilUsed,
  onCoverageUsed,
  numberCounts,
  isPaused,
  isComplete = false,
  onSectionComplete,
  playTurnOnSound,
  playTurnOffSound,
  playWritePencilSound,
  isDialogOpen = false,
  highlightNumber,
  onHighlightNumberChange
}, ref) => {
  const { t } = useTranslation();
  const [selectedCell, setSelectedCell] = useState<{ row: number; col: number }>({ row: 4, col: 4 });
  const [isPencilMode, setIsPencilMode] = React.useState(false);
  const [conflictingCells, setConflictingCells] = React.useState<{row: number, col: number}[]>([]);
  const [completedSections, setCompletedSections] = React.useState<{
    type: 'row' | 'col' | 'box' | 'full';
    index: number;
    timestamp: number;
  }[]>([]);
  const [isCoverageMode, setIsCoverageMode] = useState(false);
  const [isAutoPencilMode, setIsAutoPencilMode] = useState(false);

  // Expose reset function to parent with expanded functionality
  useImperativeHandle(ref, () => ({
    resetSelection: () => {
      setSelectedCell({ row: 4, col: 4 });
      onHighlightNumberChange(grid[4][4]);
      setIsPencilMode(false);
      setIsAutoPencilMode(false);
      setIsCoverageMode(false);
    },
    // Add this method for testing purposes
    setCompletionProcessing: (value: boolean) => {
      isCompletionProcessing.current = value;
    }
  }));

  // Add a ref to track completion state
  const isCompletionProcessing = React.useRef(false);

  const findConflictingCells = (row: number, col: number, value: number): {row: number, col: number}[] => {
    const conflicts: {row: number, col: number}[] = [];

    // Check row
    for (let c = 0; c < 9; c++) {
      if (c !== col && grid[row][c] === value) {
        conflicts.push({row, col: c});
      }
    }

    // Check column
    for (let r = 0; r < 9; r++) {
      if (r !== row && grid[r][col] === value) {
        conflicts.push({row: r, col});
      }
    }

    // Check 3x3 box
    const boxRow = Math.floor(row / 3) * 3;
    const boxCol = Math.floor(col / 3) * 3;
    for (let r = boxRow; r < boxRow + 3; r++) {
      for (let c = boxCol; c < boxCol + 3; c++) {
        if (r !== row && c !== col && grid[r][c] === value) {
          conflicts.push({row: r, col: c});
        }
      }
    }

    return conflicts;
  };

  const removePencilMarksForNumber = React.useCallback((number: number, row: number, col: number) => {
    // Skip if we're already processing completion to prevent loops
    if (isCompletionProcessing.current) return;

    // Check if there are actually any pencil marks to remove for this number
    let hasMarksToRemove = false;

    // Quick check before doing the full operation
    // Check row
    for (let c = 0; c < 9; c++) {
      if (pencilMarks[`${row}-${c}`]?.includes(number)) {
        hasMarksToRemove = true;
        break;
      }
    }

    // Check column
    for (let r = 0; r < 9 && !hasMarksToRemove; r++) {
      if (pencilMarks[`${r}-${col}`]?.includes(number)) {
        hasMarksToRemove = true;
        break;
      }
    }

    // Check 3x3 box
    const boxRow = Math.floor(row / 3) * 3;
    const boxCol = Math.floor(col / 3) * 3;
    for (let r = boxRow; r < boxRow + 3 && !hasMarksToRemove; r++) {
      for (let c = boxCol; c < boxCol + 3; c++) {
        if (pencilMarks[`${r}-${c}`]?.includes(number)) {
          hasMarksToRemove = true;
          break;
        }
      }
    }

    // Only proceed if there are actually marks to remove
    if (!hasMarksToRemove) return;

    const newPencilMarks = { ...pencilMarks };

    // Check row
    for (let c = 0; c < 9; c++) {
      const key = `${row}-${c}`;
      if (newPencilMarks[key]) {
        newPencilMarks[key] = newPencilMarks[key].filter(n => n !== number);
        if (newPencilMarks[key].length === 0) {
          delete newPencilMarks[key];
        }
      }
    }

    // Check column
    for (let r = 0; r < 9; r++) {
      const key = `${r}-${col}`;
      if (newPencilMarks[key]) {
        newPencilMarks[key] = newPencilMarks[key].filter(n => n !== number);
        if (newPencilMarks[key].length === 0) {
          delete newPencilMarks[key];
        }
      }
    }

    // Check 3x3 box
    for (let r = boxRow; r < boxRow + 3; r++) {
      for (let c = boxCol; c < boxCol + 3; c++) {
        const key = `${r}-${c}`;
        if (newPencilMarks[key]) {
          newPencilMarks[key] = newPencilMarks[key].filter(n => n !== number);
          if (newPencilMarks[key].length === 0) {
            delete newPencilMarks[key];
          }
        }
      }
    }

    onPencilMarksChange(newPencilMarks);
  }, [pencilMarks, onPencilMarksChange]);

  const checkCompletions = React.useCallback((newGrid: number[][], changedRow: number, changedCol: number) => {
    const newCompletions: typeof completedSections = [];
    const now = Date.now();
    let shouldPlaySound = false;

    // Check if all numbers are correct in their positions
    const isCorrect = (numbers: number[], solutionNumbers: number[]) =>
      numbers.length === 9 &&
      numbers.every(n => n !== 0) &&
      numbers.every((n, i) => n === solutionNumbers[i]) &&
      new Set(numbers).size === 9;

    // Only check the row, column, and box that were affected by the change
    // Check row
    if (isCorrect(newGrid[changedRow], solution[changedRow])) {
      newCompletions.push({ type: 'row', index: changedRow, timestamp: now });
      shouldPlaySound = true;
    }

    // Check column
    const column = newGrid.map(row => row[changedCol]);
    if (isCorrect(column, solution.map(row => row[changedCol]))) {
      newCompletions.push({ type: 'col', index: changedCol, timestamp: now });
      shouldPlaySound = true;
    }

    // Check box
    const boxRow = Math.floor(changedRow / 3) * 3;
    const boxCol = Math.floor(changedCol / 3) * 3;
    const boxIndex = Math.floor(changedRow / 3) * 3 + Math.floor(changedCol / 3);
    const boxNumbers = [];
    const solutionBoxNumbers = [];
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        boxNumbers.push(newGrid[boxRow + i][boxCol + j]);
        solutionBoxNumbers.push(solution[boxRow + i][boxCol + j]);
      }
    }
    if (isCorrect(boxNumbers, solutionBoxNumbers)) {
      newCompletions.push({ type: 'box', index: boxIndex, timestamp: now });
      shouldPlaySound = true;
    }

    // Check if entire puzzle is complete and correct
    if (newGrid.every((row, i) =>
      row.every((cell, j) => cell !== 0 && cell === solution[i][j])
    )) {
      isCompletionProcessing.current = true; // Set flag to prevent further state updates
      newCompletions.push({ type: 'full', index: 0, timestamp: now });
      // Don't play section complete sound for full completion
      // The parent component will play the puzzle complete sound
      shouldPlaySound = false;
    }

    if (newCompletions.length > 0) {
      setCompletedSections(newCompletions);

      // Play sound if a section was completed (but not the full puzzle)
      if (shouldPlaySound) {
        onSectionComplete();
      }

      if (!newCompletions.some(c => c.type === 'full')) {
        // For partial completions, clear after 1.5s
        setTimeout(() => setCompletedSections([]), 1500);
      } else {
        // For full completion, clear after 4.5s (3 repetitions)
        setTimeout(() => {
          setCompletedSections([]);
          // Reset the completion processing flag after all animations are done
          setTimeout(() => {
            isCompletionProcessing.current = false;
          }, 100);
        }, 4500);
      }
    }
  }, [solution, onSectionComplete]);

  const handleNumberClick = (number: number) => {
    if (selectedCell) {
      // Don't allow changing original cells
      if (originalGrid[selectedCell.row][selectedCell.col] !== 0 ||
        grid[selectedCell.row][selectedCell.col] === solution[selectedCell.row][selectedCell.col]) {
        return;
      }

      if (isPencilMode) {
        // Check if the cell has an incorrect number - if so, clear it first
        const currentValue = grid[selectedCell.row][selectedCell.col];
        if (currentValue !== 0 && currentValue !== solution[selectedCell.row][selectedCell.col]) {
          onCellChange(selectedCell.row, selectedCell.col, 0);
        }
        const cellKey = `${selectedCell.row}-${selectedCell.col}`;
        const currentMarks = pencilMarks[cellKey] || [];

        // Check if we're removing a pencil mark
        const isRemoving = currentMarks.includes(number);

        if (isRemoving) {
          // Simply remove the pencil mark without conflict checking
          const newMarks = currentMarks.filter(n => n !== number);

          // Create a new object without the key if marks are empty
          if (newMarks.length === 0) {
            const updatedMarks = { ...pencilMarks };
            delete updatedMarks[cellKey];
            onPencilMarksChange(updatedMarks, true, false, true);
          } else {
            // Only update if we have marks
            onPencilMarksChange({
              ...pencilMarks,
              [cellKey]: newMarks
            }, true, false, true);
          }
        } else {
          // Adding a pencil mark - check for conflicts
          // Check for conflicts (but still allow the move)
          const conflicts = findConflictingCells(selectedCell.row, selectedCell.col, number);

          if (conflicts.length > 0) {
            // Show conflicts but don't prevent the move
            setConflictingCells([...conflicts, selectedCell]);
            setTimeout(() => setConflictingCells([]), 1500);
          }

          // Check if this is a pencil error (adding a number that can't go here)
          const isError = !getAllowedNumbers(selectedCell.row, selectedCell.col).includes(number);
          if (isError) {
            onPencilError();
          }

          // Add the pencil mark
          const newMarks = [...currentMarks, number].sort();
          onPencilMarksChange({ ...pencilMarks, [cellKey]: newMarks }, true, isError, false);
        }
      } else {
        // Check for conflicts (but still allow the move)
        const conflicts = findConflictingCells(selectedCell.row, selectedCell.col, number);

        if (conflicts.length > 0) {
          // Show conflicts but don't prevent the move
          setConflictingCells([...conflicts, selectedCell]);
          setTimeout(() => setConflictingCells([]), 1500);
        }

        // Allow any number to be entered, even if wrong
        onCellChange(selectedCell.row, selectedCell.col, number);

        // Only remove pencil marks if the number is correct
        if (solution[selectedCell.row][selectedCell.col] === number) {
          removePencilMarksForNumber(number, selectedCell.row, selectedCell.col);
        }

        // Create new grid with the change to check completions
        const newGrid = grid.map((r, i) =>
          i === selectedCell.row
            ? r.map((c, j) => j === selectedCell.col ? number : c)
            : r
        );
        checkCompletions(newGrid, selectedCell.row, selectedCell.col);

        // Update highlight number to show matching numbers
        onHighlightNumberChange(number);
      }
    }
  };

  const handleCellClick = (row: number, col: number) => {
    // Always set the selected cell, never deselect
    setSelectedCell({ row, col });

    // Only set highlight number if the cell value matches the solution
    const cellValue = grid[row][col];
    if (cellValue !== 0) {
      onHighlightNumberChange(cellValue === solution[row][col] ? cellValue : null);
    } else {
      onHighlightNumberChange(null);
    }
  };

  const handleClearCell = () => {
    if (selectedCell) {
      if (grid[selectedCell.row][selectedCell.col] === solution[selectedCell.row][selectedCell.col]) {
        return;
      }

      onCellChange(selectedCell.row, selectedCell.col, 0);
      const cellKey = `${selectedCell.row}-${selectedCell.col}`;
      const newPencilMarks = { ...pencilMarks };
      delete newPencilMarks[cellKey];
      onPencilMarksChange(newPencilMarks);
    }
  };

  const handlePencilModeToggle = () => {
    const newMode = !isPencilMode;
    setIsPencilMode(newMode);

    // Play appropriate sound
    if (newMode) {
      playTurnOnSound();
    } else {
      playTurnOffSound();
    }
  };

  const handleCoverageModeToggle = () => {
    const newMode = !isCoverageMode;
    setIsCoverageMode(newMode);

    // Play appropriate sound
    if (newMode) {
      playTurnOnSound();
      onCoverageUsed();
    } else {
      playTurnOffSound();
    }
  };

  const handleAutoPencil = () => {
    // Toggle the auto-pencil mode
    const newMode = !isAutoPencilMode;
    setIsAutoPencilMode(newMode);

    if (newMode) {
      // Play turn on sound
      playTurnOnSound();

      // Track auto-pencil usage
      onAutoPencilUsed();

      // If activating auto-pencil mode, deactivate regular pencil mode
      setIsPencilMode(false);

      // First, clear any erroneous inputs
      const newGrid = [...grid];
      for (let row = 0; row < 9; row++) {
        for (let col = 0; col < 9; col++) {
          if (grid[row][col] !== 0 && grid[row][col] !== solution[row][col]) {
            newGrid[row][col] = 0;
            onCellChange(row, col, 0);
          }
        }
      }

      // Fill in all possible pencil marks
      const newPencilMarks: Record<string, number[]> = {};

      for (let row = 0; row < 9; row++) {
        for (let col = 0; col < 9; col++) {
          if (newGrid[row][col] === 0) { // Only for empty cells
            const allowedNumbers = getAllowedNumbers(row, col);
            if (allowedNumbers.length > 0) {
              newPencilMarks[`${row}-${col}`] = allowedNumbers;
            }
          }
        }
      }

      // Play write pencil sound
      playWritePencilSound();

      // Pass false since this is not a manual entry
      onPencilMarksChange(newPencilMarks, false);
    } else {
      // Play turn off sound
      playTurnOffSound();

      // If deactivating auto-pencil mode, clear all pencil marks
      onPencilMarksChange({}, false);
    }
  };

  // Helper function to get allowed numbers for a cell
  const getAllowedNumbers = (row: number, col: number): number[] => {
    const usedNumbers = new Set<number>();

    // Check row
    for (let c = 0; c < 9; c++) {
      if (grid[row][c] !== 0 && grid[row][c] == solution[row][c]) {
        usedNumbers.add(grid[row][c]);
      }
    }

    // Check column
    for (let r = 0; r < 9; r++) {
      if (grid[r][col] !== 0 && grid[r][col] == solution[r][col]) {
        usedNumbers.add(grid[r][col]);
      }
    }

    // Check 3x3 box
    const boxRow = Math.floor(row / 3) * 3;
    const boxCol = Math.floor(col / 3) * 3;
    for (let r = boxRow; r < boxRow + 3; r++) {
      for (let c = boxCol; c < boxCol + 3; c++) {
        if (grid[r][c] !== 0 && grid[r][c] == solution[r][c]) {
          usedNumbers.add(grid[r][c]);
        }
      }
    }

    // Return numbers that are not used
    return [1, 2, 3, 4, 5, 6, 7, 8, 9].filter(n => !usedNumbers.has(n));
  };

  // Group the grid into 3x3 subgrids
  const subgrids = Array.from({ length: 3 }, (_, i) =>
    Array.from({ length: 3 }, (_, j) => ({
      startRow: i * 3,
      startCol: j * 3,
    }))
  );

  const getCellHighlight = (row: number, col: number) => {
    const boxIndex = Math.floor(row / 3) * 3 + Math.floor(col / 3);

    // Check for full puzzle completion first
    if (completedSections.some(c => c.type === 'full')) {
      return `bg-gradient-to-r from-pink-300 via-purple-300 to-indigo-300
              dark:from-pink-600 dark:via-purple-600 dark:to-indigo-600
              motion-safe:animate-gradient bg-gradient-size`;
    }

    // Check for partial completions
    for (const completion of completedSections) {
      if ((completion.type === 'row' && completion.index === row) ||
          (completion.type === 'col' && completion.index === col) ||
          (completion.type === 'box' && completion.index === boxIndex)) {
        return `bg-gradient-to-br from-amber-200 to-orange-200
                dark:from-amber-700 dark:to-orange-700
                motion-safe:animate-pulse`;
      }
    }

    return '';
  };

  const shouldHighlightCell = (row: number, col: number) => {
    if (!highlightNumber) return false;

    // Highlight matching numbers
    if (grid[row][col] === highlightNumber) return true;

    // Only check row/col/box coverage if coverage mode is active
    if (isCoverageMode) {
      // Check if this cell is in the same row or column as any matching number
      for (let i = 0; i < 9; i++) {
        if (grid[row][i] === highlightNumber || grid[i][col] === highlightNumber) {
          return true;
        }
      }

      // Check if this cell is in the same box as any matching number
      const boxStartRow = Math.floor(row / 3) * 3;
      const boxStartCol = Math.floor(col / 3) * 3;
      for (let i = 0; i < 3; i++) {
        for (let j = 0; j < 3; j++) {
          if (grid[boxStartRow + i][boxStartCol + j] === highlightNumber) {
            return true;
          }
        }
      }
    }

    return false;
  };

  // Update the useEffect to prevent infinite loop
  React.useEffect(() => {
    if (hintedCell && !isCompletionProcessing.current) {
      const { row, col } = hintedCell;
      const value = grid[row][col];
      if (value !== 0) {
        // Remove pencil marks for this number in the affected row, column, and box
        removePencilMarksForNumber(value, row, col);
      }
    }
  }, [hintedCell, grid, removePencilMarksForNumber]);

  const keyboardActionsRef = React.useRef({
    handleNumberClick,
    handleClearCell,
    handlePencilModeToggle,
    handleCoverageModeToggle,
    handleAutoPencil
  });
  keyboardActionsRef.current = {
    handleNumberClick,
    handleClearCell,
    handlePencilModeToggle,
    handleCoverageModeToggle,
    handleAutoPencil
  };

  // Update the useEffect for keyboard handling
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle keyboard events when paused, completed, or when a dialog is open
      if (isPaused || isComplete || isDialogOpen) {
        return;
      }

      if (selectedCell) {
        let newRow = selectedCell.row;
        let newCol = selectedCell.col;
        let cellChanged = false;

        switch (e.key) {
          case 'ArrowUp':
            newRow = (newRow + 8) % 9; // Wrap around to bottom
            cellChanged = true;
            e.preventDefault(); // Prevent page scrolling
            break;
          case 'ArrowDown':
            newRow = (newRow + 1) % 9; // Wrap around to top
            cellChanged = true;
            e.preventDefault(); // Prevent page scrolling
            break;
          case 'ArrowLeft':
            newCol = (newCol + 8) % 9; // Wrap around to right
            cellChanged = true;
            e.preventDefault(); // Prevent page scrolling
            break;
          case 'ArrowRight':
            newCol = (newCol + 1) % 9; // Wrap around to left
            cellChanged = true;
            e.preventDefault(); // Prevent page scrolling
            break;
          case '1': case '2': case '3': case '4': case '5':
          case '6': case '7': case '8': case '9':
            if (!isPaused && !isComplete) {
              const num = parseInt(e.key, 10);
              keyboardActionsRef.current.handleNumberClick(num);
            }
            e.preventDefault();
            break;
          case '0': case 'Backspace': case 'Delete':
            if (!isPaused && !isComplete) {
              keyboardActionsRef.current.handleClearCell();
            }
            e.preventDefault();
            break;
          //case ' ': // Space key
            // This should be handled at the game level for pause/resume
            //e.preventDefault();
            //break;
          case 'p': case 'P': // Pencil mode
            keyboardActionsRef.current.handlePencilModeToggle();
            e.preventDefault();
            break;
          case 'c': case 'C': // Coverage mode
            keyboardActionsRef.current.handleCoverageModeToggle();
            e.preventDefault();
            break;
          case 'a': case 'A': // Auto-pencil
            keyboardActionsRef.current.handleAutoPencil();
            e.preventDefault();
            break;
          case 'h': case 'H': // Hint
            onHint();
            e.preventDefault();
            break;
          default:
            return; // Exit this handler for other keys
        }

        if (cellChanged && (newRow !== selectedCell.row || newCol !== selectedCell.col)) {
          setSelectedCell({ row: newRow, col: newCol });

          // Update highlight number based on the new cell
          const cellValue = grid[newRow][newCol];
          if (cellValue !== 0) {
            onHighlightNumberChange(cellValue === solution[newRow][newCol] ? cellValue : null);
          } else {
            onHighlightNumberChange(null);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedCell, isPaused, isComplete, grid, solution, onHint, isDialogOpen,
      onHighlightNumberChange]);

  return (
    <div className="flex flex-col items-center gap-4 max-w-md mx-auto">
      {/* Responsive board container */}
      <div className="w-full max-w-[90vw] aspect-square relative">
        {isPaused && !isComplete && (
          <div className="absolute inset-0 bg-gray-500 bg-opacity-80 dark:bg-gray-800 dark:bg-opacity-90 z-20 flex items-center justify-center rounded-lg">
            <div className="text-white text-2xl font-bold bg-blue-600 px-8 py-4 rounded-lg shadow-xl border-2 border-white dark:border-gray-300 motion-safe:animate-pulse">
              {t('paused')}
            </div>
          </div>
        )}
        <div className="w-full h-full grid grid-cols-3 grid-rows-3 gap-0 p-2 bg-gray-400 dark:bg-gray-600 rounded-lg shadow-lg">
          {subgrids.map((subgridRow, gridRowIndex) =>
            subgridRow.map((subgrid, gridColIndex) => (
              <div
                key={`subgrid-${gridRowIndex}-${gridColIndex}`}
                className="w-full h-full box-border border-2 border-gray-500 dark:border-gray-400 grid grid-cols-3 grid-rows-3 gap-[1px] sm:gap-[2px]"
              >
                {Array.from({ length: 3 }, (_, i) =>
                  Array.from({ length: 3 }, (_, j) => {
                    const row = subgrid.startRow + i;
                    const col = subgrid.startCol + j;
                    const isOriginal = originalGrid[row][col] !== 0;
                    const isSelected = selectedCell?.row === row && selectedCell?.col === col;
                    const cellKey = `${row}-${col}`;
                    const pencilNumbers = pencilMarks[cellKey] || [];

                    return (
                      <div
                        key={`${row}-${col}`}
                        data-testid={`cell-${row}-${col}`}
                        role="cell"
                        onClick={() => handleCellClick(row, col)}
                        className={cn(
                          'w-full h-full box-border',
                          'flex items-center justify-center',
                          'transition-colors duration-500 motion-reduce:transition-none',
                          'bg-white dark:bg-gray-800',
                          'border border-gray-200 dark:border-gray-700',
                          'cursor-pointer',
                          'relative', // Add relative positioning for the overlay
                          isSelected && 'bg-blue-100 dark:bg-blue-900 ring-2 ring-inset ring-cobalt dark:ring-indigo-300',
                          shouldHighlightCell(row, col) && !isSelected &&
                            'bg-blue-50 dark:bg-blue-950',
                          hintedCell?.row === row && hintedCell?.col === col &&
                            'bg-green-200 dark:bg-green-900',
                          conflictingCells.some(c => c.row === row && c.col === col) &&
                            'bg-red-200 dark:bg-red-900',
                          getCellHighlight(row, col)
                        )}
                      >
                        {/* Add cell content overlay when paused */}
                        {isPaused && !isComplete && (
                          <div className="absolute inset-0 bg-gradient-to-r from-indigo-900 via-purple-900 to-gray-900 opacity-85 z-10 rounded-sm transition-all duration-300 ease-in-out shadow-inner"></div>
                        )}

                        {grid[row][col] !== 0 ? (
                          <span
                            className={cn(
                              'text-base sm:text-xl md:text-2xl font-bold',
                              'relative z-5', // Lower z-index than the overlay
                              isOriginal
                                ? 'text-gray-900 dark:text-white'
                                : grid[row][col] === solution[row][col]
                                  ? 'text-blue-600 dark:text-blue-400'
                                  : 'text-red-700 dark:text-red-300 underline decoration-2',
                              isPaused && !isComplete && 'opacity-0' // Hide numbers when paused
                            )}
                          >
                            {grid[row][col]}
                          </span>
                        ) : (
                          <div className={cn(
                            'grid grid-cols-3 grid-rows-3 h-full gap-[1px] p-[2px] text-[8px] sm:text-[10px] relative z-5',
                            isPaused && 'opacity-0' // Hide pencil marks when paused
                          )}>
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                              <div
                                key={n}
                                className="flex items-center justify-center h-full"
                              >
                                {pencilNumbers.includes(n) && (
                                  <span className="text-gray-500 dark:text-gray-400 italic">
                                    {n}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                ).flat()}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Control buttons */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 w-full max-w-[90vw]">
        <button
          aria-pressed={isPencilMode}
          data-testid="button-pencil"
          onClick={() => !isPaused && !isComplete && handlePencilModeToggle()}
          className={cn(
            'min-h-11 border border-slate-500 dark:border-slate-400 px-2 py-2 rounded-lg text-sm font-medium',
            isPencilMode
              ? 'bg-cobalt text-white hover:!bg-blue-800'
              : 'bg-gray-200 dark:bg-gray-700',
            'hover:bg-gray-300 dark:hover:bg-gray-600',
            (isPaused || isComplete) && 'opacity-50 cursor-not-allowed'
          )}
          disabled={isPaused || isComplete}
        >
          {t('pencil')}
        </button>

        <button
          aria-pressed={isAutoPencilMode}
          data-testid="button-auto-pencil"
          onClick={() => !isPaused && !isComplete && handleAutoPencil()}
          className={cn(
            'min-h-11 border border-slate-500 dark:border-slate-400 px-2 py-2 rounded-lg text-sm font-medium',
            'bg-gray-200 dark:bg-gray-700',
            'hover:bg-gray-300 dark:hover:bg-gray-600',
            (isPaused || isComplete) && 'opacity-50 cursor-not-allowed'
          )}
          disabled={isPaused || isComplete}
        >
          {isAutoPencilMode ? t('clearPencil') : t('autoPencil')}
        </button>

        <button
          aria-pressed={isCoverageMode}
          data-testid="button-coverage"
          onClick={() => !isPaused && !isComplete && handleCoverageModeToggle()}
          className={cn(
            'min-h-11 border border-slate-500 dark:border-slate-400 px-2 py-2 rounded-lg text-sm font-medium',
            isCoverageMode
              ? 'bg-cobalt text-white hover:!bg-blue-800'
              : 'bg-gray-200 dark:bg-gray-700',
            'hover:bg-gray-300 dark:hover:bg-gray-600',
            (isPaused || isComplete) && 'opacity-50 cursor-not-allowed'
          )}
          disabled={isPaused || isComplete}
        >
          {t('coverage')}
        </button>
        <button
          data-testid="button-hint"
          onClick={() => !isPaused && !isComplete && onHint()}
          className={cn(
            'min-h-11 border border-slate-500 dark:border-slate-400 px-2 py-2 rounded-lg text-sm font-medium',
            'bg-gray-200 dark:bg-gray-700',
            'hover:bg-gray-300 dark:hover:bg-gray-600',
            (isPaused || isComplete) && 'opacity-50 cursor-not-allowed'
          )}
          disabled={isPaused || isComplete}
        >
          {t('hint')}
        </button>
      </div>

      <div className="w-full max-w-[90vw] grid grid-cols-5 sm:grid-cols-10 gap-2">
        <button
          aria-label={t('clearCell')}
          data-testid="button-clear"
          onClick={() => !isPaused && !isComplete && handleClearCell()}
            className={cn(
              'w-full min-h-11',
              'flex items-center justify-center',
              'text-sm sm:text-lg font-semibold',
              'bg-gray-200 dark:bg-gray-700',
              'rounded-lg',
              'hover:bg-gray-300 dark:hover:bg-gray-600',
              (!selectedCell || isPaused || isComplete) && 'opacity-50 cursor-not-allowed'
            )}
            disabled={!selectedCell || isPaused || isComplete}
          >
          ⎵
        </button>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((number) => {
          const isFullyUsed = numberCounts[number] >= 9;
          const isPencilNumberActive = isPencilMode &&
            selectedCell &&
            pencilMarks[`${selectedCell.row}-${selectedCell.col}`]?.includes(number);

          return (
            <div key={number} className="flex items-center">
              <button
                key={number}
                data-testid={`button-${number}`}
                onClick={() => handleNumberClick(number)}
                className={cn(
                  'w-full min-h-11',
                  'flex items-center justify-center',
                  'text-sm sm:text-lg font-semibold',
                  'rounded-lg',
                  isPencilNumberActive
                    ? 'bg-blue-300 dark:bg-blue-800'
                    : 'bg-gray-200 dark:bg-gray-700',
                  'hover:bg-gray-300 dark:hover:bg-gray-600',
                  isFullyUsed && 'opacity-50 bg-gray-400 dark:bg-gray-900',
                  (!selectedCell || isPaused || isComplete) && 'opacity-50 cursor-not-allowed',
                  isPencilMode && 'italic',
                  isPencilNumberActive && !isPaused && 'shadow-inner border-2 border-gray-400 dark:border-gray-500'
                )}
                disabled={!selectedCell || isPaused || isComplete || isFullyUsed}
              >
                {number}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
});
